import { useEffect } from "react";
import { createRootRoute, createRoute, createRouter, Navigate, Outlet, useRouterState } from "@tanstack/react-router";
import { canViewCustomer, safeRedirect } from "./lib/access";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./lib/auth";
import { useRealtimeConnection } from "./lib/realtime";
import { inBridge } from "./lib/native";
import { Loading } from "./console/ui";
import { AdminPage } from "./screens/admin";
import { BridgePage, BridgeUnavailable } from "./screens/bridge";
import { CustomerPage } from "./screens/customer";
import { LoginPage } from "./screens/login";

/**
 * 경로: /login · /admin/:screen (직원) · /c/:customerId/:screen (고객 화면) · /c/:customerId/machines/:machineId · /bridge (Electron)
 * 운영에서도 ?site= 같은 쿼리로 범위를 고르지 않는다. 범위는 경로 + 서버 권한으로만 정한다.
 */
function Root() {
  const status = useAuth((s) => s.status);
  const boot = useAuth((s) => s.boot);
  const path = useRouterState({ select: (s) => s.location.pathname });
  useRealtimeConnection(useQueryClient());
  useEffect(() => {
    void boot();
  }, [boot]);
  if (status === "booting") return <Loading label="연결 중" />;
  if (inBridge()) {
    if (status === "bridge-error") return <BridgeUnavailable />;
    if (!path.startsWith("/bridge")) return <Navigate to="/bridge/$screen" params={{ screen: "overview" }} />;
    return <Outlet />;
  }
  if (status !== "signed-in" && path !== "/login") return <Navigate to="/login" search={{ redirect: safeRedirect(path) }} />;
  return <Outlet />;
}

function Home() {
  const me = useAuth((s) => s.me);
  if (!me) return <Loading />;
  if (me.kind === "machine") return <Navigate to="/bridge" />;
  if (me.isStaff) return <Navigate to="/admin/$screen" params={{ screen: "overview" }} />;
  if (me.customerId) return <Navigate to="/c/$customerId/$screen" params={{ customerId: me.customerId, screen: "overview" }} />;
  return <Loading label="접근할 수 있는 화면이 없습니다" />;
}

function useMe() {
  return useAuth((s) => s.me);
}

const rootRoute = createRootRoute({ component: Root });
const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: Home });
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>) => ({ redirect: safeRedirect(s.redirect) }),
  component: function Login() {
    const { redirect } = loginRoute.useSearch();
    const status = useAuth((s) => s.status);
    if (status === "signed-in") return <Navigate to={redirect ?? "/"} />;
    return <LoginPage redirect={redirect} />;
  },
});
const adminIndex = createRoute({ getParentRoute: () => rootRoute, path: "/admin", component: () => <Navigate to="/admin/$screen" params={{ screen: "overview" }} /> });
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/$screen",
  component: function Admin() {
    const me = useMe();
    const { screen } = adminRoute.useParams();
    if (!me) return <Loading />;
    if (!me.isStaff) return <Navigate to="/" />;
    return <AdminPage me={me} screen={screen} />;
  },
});
const customerIndex = createRoute({
  getParentRoute: () => rootRoute,
  path: "/c/$customerId",
  component: function CustomerIndex() {
    const { customerId } = customerIndex.useParams();
    return <Navigate to="/c/$customerId/$screen" params={{ customerId, screen: "overview" }} />;
  },
});
const customerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/c/$customerId/$screen",
  component: function Customer() {
    const me = useMe();
    const { customerId, screen } = customerRoute.useParams();
    if (!me) return <Loading />;
    if (!canViewCustomer(me, customerId)) return <Navigate to="/" />;
    return <CustomerPage key={customerId} me={me} customerId={customerId} screen={screen} />;
  },
});
const machineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/c/$customerId/machines/$machineId",
  component: function Machine() {
    const me = useMe();
    const { customerId, machineId } = machineRoute.useParams();
    if (!me) return <Loading />;
    if (!canViewCustomer(me, customerId)) return <Navigate to="/" />;
    return <CustomerPage key={customerId} me={me} customerId={customerId} screen="machines" machineId={machineId} />;
  },
});
const bridgeIndex = createRoute({ getParentRoute: () => rootRoute, path: "/bridge", component: () => <Navigate to="/bridge/$screen" params={{ screen: "overview" }} /> });
const bridgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bridge/$screen",
  component: function Bridge() {
    const me = useMe();
    const { screen } = bridgeRoute.useParams();
    if (!me) return <Loading />;
    // 데스크톱 화면은 머신 계정(Electron)만. 브라우저의 사람 계정은 들어올 수 없다.
    if (me.kind !== "machine") return <Navigate to="/" />;
    return <BridgePage me={me} screen={screen} />;
  },
});

const routeTree = rootRoute.addChildren([indexRoute, loginRoute, adminIndex, adminRoute, customerIndex, customerRoute, machineRoute, bridgeIndex, bridgeRoute]);
export const router = createRouter({ routeTree, defaultNotFoundComponent: () => <Navigate to="/" /> });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
